import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';

import type { FetchFunction } from '@xing-com/crate-core-fetch';
import type { Runtime } from '@xing-com/crate-xinglet/internal';

// NOTE: importing the fragment type here violates some settings, but we want to
// have them in the repo root
// eslint-disable-next-line node/no-unpublished-import
import possibleTypes from '../../../fragment-types.json';

export function createApolloClient(runtime: Runtime): ApolloClient<unknown> {
  const { loginState } = runtime.config;
  const allowDataFetching = !runtime.isServer || loginState === 'LOGGED_OUT';
  const { APOLLO_STATE: state } = runtime.serverData;
  const defaultOptions = runtime.isServer
    ? // NOTE: Apollo keeps Suspense query refs alive for 30s by default. On the
      // server that retains request-scoped clients and caches far longer than
      // the render needs them.
      { react: { suspense: { autoDisposeTimeoutMs: 0 } } }
    : undefined;

  const cache = new InMemoryCache({ possibleTypes });

  if (state) cache.restore({ ...state });

  const configuredFetch: FetchFunction = async (...args) => {
    if (!allowDataFetching) {
      throw new Error(
        'Data fetching is not allowed! Please wrap your component in <NoSSRLoggedInSuspense>.'
      );
    }

    return runtime.fetch(...args);
  };

  const client = new ApolloClient({
    cache,
    connectToDevTools: runtime.host.isPreview,
    defaultOptions,
    link: new HttpLink({
      fetch: Object.assign(configuredFetch, globalThis.fetch),
      uri: runtime.config.xingOneEndpoint,
    }),
    ssrMode: runtime.isServer,
  });

  if (runtime.isServer) {
    runtime.events.once('server-rendered', () => {
      runtime.serverData['APOLLO_STATE'] = cache.extract();
    });
  }

  return client;
}
